import React, { useEffect, useState } from "react";
import "./VoucherRequest.css";
import ReusableTable, {
  Column,
} from "../../../components/reusable-table/ReusableTable";
import { getFileById } from "../../../apis/getFile";
import Modal from "./voucherRequest-modal/Modal";
import { formatName } from "../../../utils/formatName";
import {
  getProctorRequests,
  updateProctorRequestStatus,
} from "../../../apis/voucherRequestApis";
import { showSnackbar } from "../../../redux/slices/snackbarSlice";
import { useDispatch } from "react-redux";
import { BiExport } from "react-icons/bi";
import Button from "../../../components/reusable-button/Button";
import * as XLSX from "xlsx";
import { AiOutlineEye } from "react-icons/ai";

const ExportIcon = BiExport as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
const EyeOpenIcon = AiOutlineEye as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

interface FileDetails {
  fileId: number;
  uuid: string;
  name: string;
  maskName: string;
  path: string;
}

interface Voucher {
  id: string;
  fullName: string;
  emailAddress: string;
  submittedAt: string;
  updatedAt: string;
  status: "pending" | "approved" | "reject";
  message: string;
  ageEighteenPlus: boolean;
  phoneNumber: string;
  cameraAvailable: boolean;
  photoId: boolean;
  parentConsentUploadDetails?: FileDetails;
  ProctorRequestFileMappings: {
    id: string;
    fileId: number;
    files: FileDetails;
    maskName: string;
  }[];
  modules: {
    moduleName: string;
  };
}

const VoucherRequest = () => {
  const dispatch = useDispatch();
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [activeTab, setActiveTab] = useState<"pending" | "approved" | "reject">(
    "pending"
  );
  const [modalType, setModalType] = useState<
    "image" | "reject" | "approve" | null
  >(null);
  const [modalImages, setModalImages] = useState<
    { src: string; maskName: string }[]
  >([]);
  const [rejectReason, setRejectReason] = useState("");
  const [approveMailBody, setApproveMailBody] = useState("");
  const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);

  useEffect(() => {
    const fetchVouchers = async () => {
      const response = await getProctorRequests();
      if (response.success && response.data) {
        setVouchers(response.data);
      } else {
        console.error(response.message || "Failed to fetch voucher requests");
      }
    };

    fetchVouchers();
  }, []);

  const openModal = (id: string, type: "image" | "reject" | "approve") => {
    setSelectedVoucherId(id);
    setModalType(type);
  };

  const handleApprove = async () => {
    if (!selectedVoucherId) return;

    setLoaderOpen(true);
    try {
      const res = await updateProctorRequestStatus(
        selectedVoucherId,
        "approved",
        approveMailBody
      );
      const response = await getProctorRequests();
      if (response.success && response.data) {
        setVouchers(response.data);
      }
      dispatch(
        showSnackbar({
          message: res.data || "Request approved successfully!",
          type: "success",
        })
      );
      setActiveTab("approved");
      setModalType(null);
      setApproveMailBody("");
    } catch {
      dispatch(
        showSnackbar({
          message: "Failed to approve request",
          type: "error",
        })
      );
    }
    setLoaderOpen(false);
  };

  const confirmReject = async () => {
    if (!selectedVoucherId || !rejectReason.trim()) return;
    setLoaderOpen(true);
    try {
      const res = await updateProctorRequestStatus(
        selectedVoucherId,
        "reject",
        rejectReason
      );
      const response = await getProctorRequests();
      if (response.success && response.data) {
        setVouchers(response.data);
      }
      dispatch(
        showSnackbar({
          message: res?.data || "Request rejected successfully!",
          type: "success",
        })
      );
      setActiveTab("reject");
    } catch {
      dispatch(
        showSnackbar({
          message: "Failed to reject request",
          type: "error",
        })
      );
    } finally {
      setModalType(null);
      setRejectReason("");
      setSelectedVoucherId(null);
    }
    setLoaderOpen(false);
  };

  const handleExport = () => {
    const exportData = vouchers
      .filter((v) => v.status === "approved" || v.status === "reject")
      .map((v) => ({
        "Full Name": v.fullName,
        Email: v.emailAddress,
        Phone: v.phoneNumber,
        Exam: v.modules.moduleName,
        "Above 18": v.ageEighteenPlus ? "YES" : "NO",
        "Photo ID": v.photoId ? "YES" : "NO",
        "Camera Available": v.cameraAvailable ? "YES" : "NO",
        Status: v.status === "approved" ? "Approved" : "Rejected",
        "Requested At": new Date(v.submittedAt).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
        [v.status === "approved" ? "Approved At" : "Rejected At"]: new Date(
          v.updatedAt
        ).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
      }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Voucher Requests");

    XLSX.writeFile(workbook, "voucher_requests.xlsx");
  };

  const handleSingleImagePreview = async (uuid: string, maskName: string) => {
    setModalType("image");
    setLoading(true);
    try {
      const res = await getFileById(uuid);
      if (res.success && res.data) {
        setModalImages([{ src: res.data, maskName }]);
      }
    } catch {
      dispatch(
        showSnackbar({
          message: "Failed to load image",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredVouchers = vouchers.filter((v) => v.status === activeTab);

  const baseColumns: Column<Voucher>[] = [
    {
      header: "User Details",
      render: (row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <strong style={{ color: "#1f4d4d" }}>
            {formatName(row.fullName)}
          </strong>
          <span>{row.emailAddress}</span>
          <span>{row.phoneNumber}</span>
        </div>
      ),
    },
    {
      header: "Exam",
      render: (row) => row.modules.moduleName,
    },
    {
      header: "Request Date",
      render: (row) =>
        new Date(row.submittedAt).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          // hour: "2-digit",
          // minute: "2-digit",
          // second: "2-digit",
        }),
    },
    {
      header: "Over 18",
      render: (row) =>
        row.ageEighteenPlus ? (
          "YES"
        ) : row.parentConsentUploadDetails ? (
          <>
            <span>NO</span>
            <button
              onClick={() =>
                handleSingleImagePreview(
                  row.parentConsentUploadDetails!.uuid,
                  row.parentConsentUploadDetails!.maskName
                )
              }
              style={{
                background: "none",
                border: "none",
                color: "#1f4d4d",
                textDecoration: "underline",
                cursor: "pointer",
                padding: 0,
              }}
            >
              PC-Form
            </button>
          </>
        ) : (
          "NO"
        ),
    },
    {
      header: "Photo ID",
      render: (row) => (row.photoId ? "YES" : "NO"),
    },
    {
      header: "System with Camera",
      render: (row) => (row.cameraAvailable ? "YES" : "NO"),
    },
    {
      header: "Screenshots of Passing Test",
      render: (row) =>
        row.ProctorRequestFileMappings?.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {row.ProctorRequestFileMappings.map((file, index) => (
              <button
                key={file.id}
                onClick={() =>
                  handleSingleImagePreview(
                    file.files?.uuid,
                    file.files?.maskName
                  )
                }
                style={{
                  background: "none",
                  border: "none",
                  color: "#1f4d4d",
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                File {index + 1}
              </button>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
  ];

  const actionColumn: Column<Voucher> = {
    header: "Action",
    render: (row) => (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <button
          onClick={() => openModal(row.id, "approve")}
          style={{
            border: "1px solid #1f4d4d",
            backgroundColor: "#1f4d4d",
            color: "white",
            padding: "6px 12px",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Approve
        </button>
        <button
          onClick={() => openModal(row.id, "reject")}
          style={{
            border: "1px solid #1f4d4d",
            backgroundColor: "#fff",
            color: "#1f4d4d",
            padding: "6px 12px",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Reject
        </button>
      </div>
    ),
  };

  const updatedAtColumn: Column<Voucher> = {
    header: activeTab === "approved" ? "Approved At" : "Rejected At",
    render: (row) =>
      new Date(row.updatedAt).toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        // hour: "2-digit",
        // minute: "2-digit",
        // second: "2-digit",
      }),
  };
  const rejectReasonColumn: Column<Voucher> = {
    header: "",
    render: (row) => (
      <div className="tooltip-wrapper">
        <EyeOpenIcon className="tooltip-icon" />
        <span className="tooltip-text">
          {row?.status === "reject" ? row?.message : ""}
        </span>
      </div>
    ),
  };

  let columns: Column<Voucher>[] = [...baseColumns];

  if (activeTab === "pending") {
    columns.push(actionColumn);
  } else if (activeTab === "approved") {
    columns.push(updatedAtColumn);
  } else if (activeTab === "reject") {
    columns.push(updatedAtColumn);
    columns.push(rejectReasonColumn);
  }

  return (
    <div className="voucher-wrapper">
      <div
        className="voucher-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: "#000" }}>
          Voucher Requests
        </h2>
        <div>
          <Button
            label="Export"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", margin: "20px 0 0" }}>
        {["pending", "approved", "reject"].map((status, index) => {
          const isActive = activeTab === status;
          return (
            <div
              key={status}
              onClick={() => setActiveTab(status as Voucher["status"])}
              style={{
                padding: "10px 20px",
                cursor: "pointer",
                backgroundColor: isActive ? "#1f4d4d" : "#f0f0f0",
                color: isActive ? "#fff" : "#000",
                fontWeight: isActive ? "bold" : "normal",
                borderTopLeftRadius: index === 0 ? 8 : 0,
                borderTopRightRadius: index === 2 ? 8 : 0,
                border: "1px solid #ccc",
                borderBottom: isActive ? "none" : "1px solid #ccc",
              }}
            >
              {status === "pending"
                ? "New Request"
                : status === "approved"
                ? "Accepted"
                : "Rejected"}
            </div>
          );
        })}
      </div>

      <ReusableTable columns={columns} rows={filteredVouchers} />

      {/* Modal */}
      <Modal
        isOpen={!!modalType}
        onClose={() => {
          setModalType(null);
          setApproveMailBody("");
          setRejectReason("");
        }}
        modalType={modalType}
        loading={loading}
        modalImages={modalImages}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        handleApprove={handleApprove}
        confirmReject={confirmReject}
        setApproveMailBody={setApproveMailBody}
        approveMailBody={approveMailBody}
        btnLoading={loaderOpen}
      />
    </div>
  );
};

export default VoucherRequest;
